<template>
  <div>
    <slot />
    <slot name="defaultFields">
      <!-- EISENHOWER-PRIORITY -->
      <EventActionFieldRowComponent
        v-if="showEisenhowerPriority === true"
        :label="t('components.issueModal.eisenhowerPriority')"
      >
        <InputRow v-slot="{ field: inputField }" name="eisenhowerPriority">
          <EisenhowerSelector
            v-bind="inputField"
            v-model="event.eisenhowerPriority"
            :disabled="disableEvent"
          />
        </InputRow>
      </EventActionFieldRowComponent>
      <EventActionFieldRowComponent :label="t('components.issueModal.area')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="eventFlags"
          name="flags"
        >
          <SearchSelect
            v-bind="inputField"
            v-model="eventFlags"
            :mode="SelectMode.FLAG"
            class="w-100"
            :filters="{
              siteIds: siteId,
              type: flagTypes,
            }"
            :disabled="disableEvent"
            :state="valid"
            :multiple="true"
          />
        </InputRow>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent :label="t('components.issueModal.title')">
        <InputRow v-slot="{ field: inputField }" v-model="event.title" name="title" required>
          <InputText
            v-bind="inputField"
            v-model="event.title"
            data-testid="IdeaTitle"
            class="w-100"
            autofocus
            :disabled="disableEvent"
          />
        </InputRow>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent :label="t('components.issueModal.user')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid, dirty } }"
          v-model="event.ownerId"
          name="ownerId"
          required
        >
          <SearchSelect
            v-bind="inputField"
            v-model="event.ownerId"
            data-testid="IdeaOwner"
            :mode="SelectMode.USER"
            :filters="{ availableOnSiteIds: siteIdArray }"
            :disabled="
              disableEvent ||
              (dirty == false &&
                create === false &&
                checkHideResponsibleRight(event.ownerId) === false)
            "
            :state="disableEvent ? null : valid"
            class="w-100"
            :value-overlay-label="
              dirty == false &&
              create === false &&
              checkHideResponsibleRight(event.ownerId) === false
                ? t('global.hidden')
                : undefined
            "
            @update:model-value="changeFirstUserToOwner"
          />
        </InputRow>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent :label="t('components.issueModal.date')">
        <DatePickerComponentNew
          v-model="occurrenceDate"
          :disabled="disableEvent || dateDisabled"
          data-testid="IdeaOccurrenceDate"
          class="border-datepicker"
        />
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent :label="t('components.issueModal.idea.isDescription')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="event.ideaIs"
          name="ideaIs"
          required
          :rules="[minLength(1)]"
        >
          <InputTextArea
            v-bind="inputField"
            v-model="event.ideaIs"
            data-testid="IdeaIs"
            class="w-100"
            auto-resize
            rows="4"
            :state="disableEvent ? null : valid"
            :disabled="disableEvent"
            @keydown.enter="handleEnter"
          />
        </InputRow>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent :label="t('components.issueModal.idea.should')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="event.ideaShould"
          name="ideaShould"
          required
          :rules="[minLength(1)]"
        >
          <InputTextArea
            v-bind="inputField"
            v-model="event.ideaShould"
            data-testid="IdeaShould"
            class="w-100"
            auto-resize
            rows="4"
            :state="disableEvent ? null : valid"
            :disabled="disableEvent"
            @keydown.enter="handleEnter"
          />
        </InputRow>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent>
        <div class="w-100 flex">
          <div class="w-75 flex">
            {{ t("global.name") }}
          </div>
          <div class="w-25 h-100 flex">
            {{ t("components.issueModal.idea.percentage") }}
            <InfoTooltip :text="t('components.issueModal.idea.percentageToolTip')" class="ml-1" />
          </div>
        </div>
      </EventActionFieldRowComponent>

      <EventActionFieldRowComponent
        v-for="(user, userIndex) in eventUsers"
        :key="`user${userIndex}`"
        :label="`${t('components.issueModal.idea.users')} ${userIndex + 1}`"
      >
        <div class="flex justify-content-between">
          <InputRow
            v-slot="{ field: inputField, validation: { dirty } }"
            :model-value="user.id"
            :name="`eventUsers${userIndex}`"
          >
            <SearchSelect
              v-bind="inputField"
              v-model="user.id"
              class="w-75 pr-1"
              :mode="SelectMode.USER"
              :filters="{ availableOnSiteIds: siteIdArray }"
              :exclude="UserAlreadyPartner"
              :disabled="
                userIndex === 0 ||
                disableEvent ||
                (create === false &&
                  dirty == false &&
                  checkHideResponsibleRight(user.id) === false &&
                  (user.id ?? 0) > 0)
              "
              :value-overlay-label="
                dirty == false && create === false && checkHideResponsibleRight(user.id) === false
                  ? t('global.hidden')
                  : undefined
              "
              @update:model-value="userSelected(user, userIndex)"
            />
          </InputRow>

          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            v-model="user.percentage"
            :name="`percentage${userIndex}`"
            :hide-error-message="true"
            :rules="[
              minValue(0),
              manualValidation<number>(isPercentageSum100Percent, {
                messageKey: 'validation.matches',
              }),
            ]"
          >
            <LISAInputNumber
              v-bind="inputField"
              v-model="user.percentage"
              :min="0"
              style="height: 38px"
              :disabled="disableEvent || user.id == null || user.id === 0"
              :class="{ 'p-invalid': isPercentageSum100Percent === false || valid === false }"
            />
          </InputRow>
        </div>
      </EventActionFieldRowComponent>

      <InputRow
        v-model="isPercentageSum100Percent"
        name="isPercentageSum100Percent"
        display-name="isPercentageSum100Percent"
        hide-label
        hide-error-message
        :rules="[
          manualValidation<boolean>(isPercentageSum100Percent, {
            messageKey: 'validation.matches',
          }),
        ]"
      >
        <Checkbox v-model="isPercentageSum100Percent" style="display: none" binary />
      </InputRow>

      <EventActionFieldRowComponent :label="t('components.issueModal.idea.category')">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="event.hlpCategoryId"
          name="hlpCategoryId"
          required
        >
          <SearchSelect
            v-bind="inputField"
            v-model="event.hlpCategoryId"
            :mode="SelectMode.IDEA_CATEGORY"
            data-testid="IdeaCategory"
            class="w-100"
            required
            :state="disableEvent ? undefined : valid"
            :disabled="disableEvent"
          />
        </InputRow>
      </EventActionFieldRowComponent>
    </slot>
    <EventActionFieldRowComponent>
      <FMFileInlineUploadPreview
        v-model:files-ids="event.fileIds"
        :disabled="disableEvent"
        automatic-screenshot
      />
    </EventActionFieldRowComponent>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, inject, type PropType } from "vue";
import InputText from "primevue/inputtext";
import InputTextArea from "primevue/textarea";
import { useStore } from "vuex";
import {
  CreateInjectionKey,
  DisableEventInjectionKey,
} from "@/components/event-action/issue/IssueTypes";
import injectAsserted from "@/functions/assert/injectAsserted";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { FlagTypes, type IHLPUserPercentage } from "@/graphql";
import { DefaultDateInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import type { IdeaEditType } from "@/components/event-action/event/modal/EventEditType";
import notEmpty from "@/functions/notEmpty";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import EisenhowerSelector from "@/components/event-action/event/eisenhowerMatrix/EisenhowerSelector.vue";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import Checkbox from "primevue/checkbox";
import { DateTime } from "luxon";
import FMFileInlineUploadPreview from "@/modules/filemanagement/components/FMFileInlineUploadPreview.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import { manualValidation, minLength, minValue } from "@/modules/formValidation";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";

const props = defineProps({
  event: {
    type: Object as PropType<IdeaEditType>,
    required: true,
  },
  labelCol: {
    type: Number,
    default: 2,
  },
});
const emit = defineEmits(["save"]);

const eventFlags = computed<number[]>({
  get() {
    return props.event.flags.map((flag) => flag.id);
  },
  set(value: number[]) {
    props.event.flags = value.map((id) => ({ id }));
  },
});
const store = useStore();
const userId = computed(() => store.getters["login/userId"]);
const create = injectAsserted(CreateInjectionKey);
const { siteId } = useProvidedBoardContext();
const siteIdArray = computed(() => (siteId.value == null ? undefined : [siteId.value]));

const occurrenceDate = computed({
  get() {
    if (props.event.occurrenceDate == null) {
      return undefined;
    }
    return DateTime.fromISO(props.event.occurrenceDate);
  },
  set(val) {
    props.event.occurrenceDate = (val ?? DateTime.now()).toISO();
  },
});

const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: "", editable: true })),
);
const dateDisabled = computed(() => !defaultDate.value.editable);

const disableEventInjected = injectAsserted(DisableEventInjectionKey);

const disableEvent = computed(() => disableEventInjected.value);

type EditUserType = { id: number | undefined; percentage: number };
const eventUsers = computed<EditUserType[]>({
  get() {
    const users: EditUserType[] = [...props.event.users];
    if (!disableEvent.value) {
      users.push({ id: undefined, percentage: 0 });
    }
    return users;
  },
  set(val) {
    props.event.users = val.filter((el): el is IHLPUserPercentage => el.id != null);
  },
});

function userSelected(user: EditUserType, userIndex: number) {
  if (eventUsers.value.length === userIndex + 1) {
    if (user.id != null) {
      props.event.users.push({ id: user.id, percentage: user.percentage });
    }
  } else if (user.id == null || user.id === 0) {
    props.event.users.splice(userIndex, 1);
  }
}
const UserAlreadyPartner = computed(() => {
  return eventUsers.value.map((el) => el.id).filter(notEmpty);
});

const isPercentageSum100Percent = computed(
  () => props.event.users.reduce((a, b) => a + b.percentage, 0) === 100,
);

const flagTypes = computed(() => {
  if (create.value) {
    return [FlagTypes.userFlag, FlagTypes.mailDistribution, FlagTypes.ideaWorld];
  }
  return [FlagTypes.userFlag];
});

function changeFirstUserToOwner(newOwnerId: number | null | undefined) {
  if (newOwnerId == null) return;
  if (props.event.users.length === 0) {
    props.event.users.push({
      id: newOwnerId,
      percentage: 100,
    });
  } else {
    props.event.users[0].id = newOwnerId;
  }
}
function handleEnter(e: KeyboardEvent) {
  if (e.metaKey || e.ctrlKey) {
    emit("save", e.shiftKey);
  }
}

function checkHideResponsibleRight(id?: number) {
  const right = store.getters["login/hasAccessRight"]("hlp.hideResponsible") ?? false;
  return right || id === userId.value;
}

const { showEisenhowerPriority } = useSQDCGlobalSettings();

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
